/**
 * Putting an app on the internet.
 *
 * One route: POST /apps/{appId}/web, which takes the document itself. There is no S3
 * presign and no direct-to-bucket PUT any more — one path, so there is no second way for a
 * document to reach the internet and no direct-bucket link to leak.
 *
 * The 300KB cap is checked HERE before the round trip, so someone is told their app is too
 * large by the thing they clicked rather than by a failure after a long upload. It counts
 * BYTES, matching the server's own Buffer.byteLength check exactly — approximating it with
 * string length would disagree with the server on every app containing an em dash.
 */
import type { AppDefinition } from '@/types/app';
import { createApp } from './apps';
import { mergeAssets, type MediaAsset } from './assets';
import { ApiError, request } from './client';
import { addManagedRow } from './managedRows';
// Pure and shared with the phone: the gate's starting-row reader and the date-token resolver.
import { seedOwnerContent, type SeedClient } from '@/webapp/seedOwnerContent';

export type { MediaAsset } from './assets';

/** What `fetch` actually puts on the wire, and what the server measures. */
export function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** The server's limit, mirrored so the refusal can happen before the upload. */
export const MAX_DOCUMENT_BYTES = 300_000;

export interface HostedApp {
  webPublished: boolean;
  webVisibility: 'public' | 'password';
  slug: string;
  /** The server confirming it actually STORED the document. */
  htmlHosted?: boolean;
  siteUrl?: string;
}

export interface HostInput {
  appId: string;
  html: string;
  /**
   * Sent only when set. Absent is a CONTENT update: the server replaces the document and keeps
   * the page's on/off state, visibility and password as they are (a page never put up before
   * still goes up public). Defaulting this to 'public' re-opened every page its owner had
   * taken offline or put behind a password, on every edit.
   */
  visibility?: 'public' | 'password';
  password?: string;
  schemas?: unknown;
  aiAccess?: string;
  /**
   * The app's creator media, name → address: the WHOLE list, which the server stores on the
   * catalogue row and hands the page as `window.__SADDLE_MEDIA__` so `S.media(name)` resolves.
   * Absent keeps the row's list as it is; sent, it replaces it.
   */
  assets?: MediaAsset[];
}

export async function hostApp(input: HostInput): Promise<HostedApp> {
  if (!input.appId) {
    throw new ApiError('misconfigured', 'This app has no id to publish under.');
  }

  const bytes = byteLength(input.html);
  if (bytes > MAX_DOCUMENT_BYTES) {
    const kb = Math.ceil(bytes / 1000);
    throw new ApiError(
      'refused',
      `This app is too large to publish — it is ${kb}KB and the limit is 300KB. Removing some seeded content usually brings it under.`,
    );
  }

  const hosted = await request<HostedApp>(`/apps/${encodeURIComponent(input.appId)}/web`, {
    method: 'POST',
    body: {
      ...(input.visibility ? { visibility: input.visibility } : {}),
      ...(input.password ? { password: input.password } : {}),
      html: input.html,
      ...(input.schemas ? { schemas: input.schemas } : {}),
      ...(input.aiAccess ? { aiAccess: input.aiAccess } : {}),
      ...(input.assets ? { assets: input.assets } : {}),
    },
  });

  /*
   * Do not trust siteUrl without htmlHosted. The server sends the URL either way, and a URL
   * for a document that was not stored is a link to an empty app — worse than no link,
   * because the owner shares it.
   */
  if (!hosted.htmlHosted) {
    throw new ApiError('server', 'The app was registered but its page was not stored. Try publishing again.');
  }

  return hosted;
}

export interface PublishedApp {
  /** The catalogue id this app is published under. Store it on the project. */
  appId: string;
  /** The address to bookmark. */
  siteUrl: string;
  /**
   * Whether the page is being SERVED after this publish. A content update keeps a page its
   * owner took offline offline, so "published" must not be read as "live".
   */
  webPublished: boolean;
  /**
   * Who may open it, as the SERVER says after this publish — so the status beside the address
   * is its answer, not a guess from which button was pressed. A content update keeps a password
   * page behind its password, and the server says so here. Absent from an older server.
   */
  webVisibility?: 'public' | 'password';
}

/**
 * The whole publish: register the app if it is not registered yet, then host the document.
 *
 * Both halves in one place because doing only the second is a 404, and that is exactly
 * what shipped — every publish from this client posted to `/apps/{document id}/web`, an id
 * the catalogue has never heard of, and the finished app never reached the internet.
 *
 * `hostedAppId` is the catalogue id once an app has one, and it is reused for the life of
 * the app: a second `POST /apps` would mint a second listing, which collides on the slug
 * and gets suffixed, so the published address would move on every re-publish.
 */
export async function publishToSaddle(input: {
  definition: AppDefinition;
  name: string;
  description?: string;
  html: string;
  /**
   * The collections this app declares, and what each field means.
   *
   * Optional, and when it is absent the server reads the document instead — which works and
   * can only ever say every field is text, because source code shows a field being written
   * and not what it IS. Sent, it is the difference between data somebody can later ask a
   * question about and a pile of strings.
   */
  schemas?: unknown;
  /**
   * Set only for an explicit decision to put the page up ("Publish", "Publish it again").
   * Absent, an app that already exists gets a content update that keeps its page as it is,
   * and an app registered here — which has no earlier choice to keep — goes up public.
   */
  visibility?: 'public' | 'password';
  /** With `visibility: 'password'`: the password the page goes up behind. */
  password?: string;
  /**
   * The app's photos, the WHOLE list, sent with the page.
   *
   * The page resolves `S.media(name)` from the catalogue row's list, and the row's list is
   * written only by a publish that carries one (absent = keep). The web builder sent none, so a
   * photo attached here was uploaded and recorded in this browser and never reached the page —
   * `S.media` answered null and the page silently showed no photo.
   */
  assets?: MediaAsset[];
  /**
   * Uploads that need the catalogue id — a NEW app's photos, which cannot go up before the app
   * is registered. Called with the id after registering and BEFORE the page is hosted, so the
   * page never goes up without them; what it answers is added to `assets` and sent with the
   * page. The phone's order (saddleHost: create, upload, publishToWeb with assets). Called
   * again with the new id when the app this project pointed at turns out to be gone.
   */
  attach?: (appId: string) => Promise<MediaAsset[]>;
  /**
   * A catalogue id an EARLIER publish of this app registered and never put a page up under —
   * what `registeredAppIdOf` read off that publish's error. Used only when the record has no
   * `hostedAppId`: the server publishes a row that was never switched on as a first publish, so
   * reusing it keeps the app's address, where registering again found the slug taken by the
   * orphan and moved the page to `<slug>-2` (F21, 2026-09-30).
   */
  registeredAppId?: string;
}): Promise<PublishedApp> {
  const existing = input.definition.hostedAppId;
  /** Reused from an earlier attempt: never hosted, so this publish is still its first. */
  const kept = existing ? undefined : input.registeredAppId || undefined;
  const register = (): Promise<string> =>
    createApp({ name: input.name, description: input.description, definition: input.definition });
  const host = async (appId: string, fresh: boolean) => {
    const attached = input.attach ? await input.attach(appId) : [];
    const assets = input.assets || attached.length ? mergeAssets(input.assets ?? [], attached) : undefined;
    return hostApp({
      appId,
      html: input.html,
      ...(input.schemas ? { schemas: input.schemas } : {}),
      ...(input.visibility ? { visibility: input.visibility } : fresh ? { visibility: 'public' as const } : {}),
      ...(input.visibility === 'password' && input.password ? { password: input.password } : {}),
      ...(assets ? { assets } : {}),
    });
  };

  let appId = existing ?? kept ?? (await register());
  /**
   * Minted in THIS call, or kept from an attempt that never put a page up — the only apps
   * certain to have no history, so the only ones seeded.
   */
  let registeredHere = !existing;
  let hosted: HostedApp;
  try {
    try {
      hosted = await host(appId, !existing);
    } catch (err) {
      /*
       * The app this project points at can be gone — deleted from the catalogue, or wiped
       * with an account. Reusing a dead id would fail the publish and leave the owner with
       * a link that 404s, so register a fresh one; the slug is reclaimed if it was freed.
       * Only for a MISSING app: a refusal, a 403 or a network failure must surface as
       * itself rather than quietly minting a second listing. The same for an id kept from an
       * earlier attempt that has since gone.
       */
      const reused = existing ?? kept;
      if (!reused || !(err instanceof ApiError && err.kind === 'not_found')) throw err;
      appId = await register();
      registeredHere = true;
      hosted = await host(appId, true);
    }
  } catch (err) {
    // The id goes with the failure, so the next publish reuses it rather than minting another.
    throw registeredHere ? withRegisteredAppId(err, appId) : err;
  }

  /*
   * The starting rows, for an app registered in this call — and only then (F1, 2026-09-30).
   *
   * The web never seeded, so every `S.list` the model filled (the wedding's gallery and
   * schedule, the café's menu) painted once and then emptied: the live read answered with the
   * server's rows, and there were none. After the host call, because that call is what declares
   * the collections a row can be written to.
   *
   * Never on a republish. An empty list by then is the owner's content — they may have deleted
   * the samples — and "empty" cannot tell that from "never seeded" (the phone's rule, saddleHost
   * `if (createdAppId)`). Best effort: the page is up, and a refused row costs an empty list,
   * not the link.
   */
  if (registeredHere) {
    try {
      await seedOwnerContent(appId, input.definition.source ?? '', seedClient(appId));
    } catch {
      /* the page is live either way */
    }
  }

  if (!hosted.siteUrl) {
    throw new ApiError('server', 'The app was published but came back without an address. Try again.');
  }
  // Absent reads as live, as on the phone: an older server always switched the page on.
  return {
    appId,
    siteUrl: hosted.siteUrl,
    webPublished: hosted.webPublished !== false,
    ...(hosted.webVisibility ? { webVisibility: hosted.webVisibility } : {}),
  };
}

/**
 * The same error — its kind, status and sentence untouched — carrying the id it registered, which
 * `registeredAppIdOf` (state/unhostedApp.ts) reads back for the next publish of the same app.
 */
function withRegisteredAppId(err: unknown, appId: string): unknown {
  if (err && typeof err === 'object') return Object.assign(err, { registeredAppId: appId });
  return err;
}

/**
 * The seeder's view of an app's collections: the first row of one (is it empty?), and a way to
 * add one. The web's own managed-rows client, because the phone's reaches React Native.
 */
function seedClient(appId: string): SeedClient {
  return {
    listRecords: async (entity, opts) => {
      const body = await request<{ items?: unknown[] } | undefined>(
        `/apps/${encodeURIComponent(appId)}/${encodeURIComponent(entity)}?limit=${opts?.limit ?? 1}`,
      );
      return { items: body?.items ?? [] };
    },
    createRecord: (entity, data) => addManagedRow(appId, entity, data),
  };
}

/** What the server says about who can open a page, after it was asked to change that. */
export interface SiteAccessResult {
  webVisibility: 'public' | 'password';
  siteUrl?: string;
}

/**
 * Put a page that is up behind a password, or take its password away — and nothing else.
 *
 * The phone's Set a password and Make public (`doPublish` in src/app/(main)/project/[id].tsx):
 * the publish route, naming a visibility and sending NO document, so the page keeps exactly the
 * content it has and only who may open it changes. Re-assembling and re-sending the app to change
 * a password would be a second, slower way to publish content, from a button about access.
 *
 * Naming a visibility also switches the page ON (`publishWeb`), which is why this is offered only
 * for a page the server says is up. A page that is offline comes back through a publish of its
 * content (`publishLatest` with `putOnline`), with a password or without.
 *
 * Success is what the SERVER says the page now is. A status that read "Password" over a page the
 * server did not confirm was behind one would be telling an owner their page is private when it
 * may not be — so anything short of that answer is a failure, said as one.
 */
export async function setSiteAccess(
  appId: string,
  access: { visibility: 'public' } | { visibility: 'password'; password: string },
): Promise<SiteAccessResult> {
  if (!appId) {
    throw new ApiError('misconfigured', 'This app has no id to publish under.');
  }
  if (access.visibility === 'password' && !access.password) {
    throw new ApiError('refused', 'Choose a password first.');
  }
  const answer = await request<{ webPublished?: boolean; webVisibility?: 'public' | 'password'; siteUrl?: string }>(
    `/apps/${encodeURIComponent(appId)}/web`,
    {
      method: 'POST',
      body: access.visibility === 'password'
        ? { visibility: 'password', password: access.password }
        : { visibility: 'public' },
    },
  );
  if (answer.webPublished !== true || answer.webVisibility !== access.visibility) {
    throw new ApiError(
      'server',
      access.visibility === 'password'
        ? 'The server did not confirm the password, so your page may not be behind one. Try again.'
        : 'The server did not confirm that, so your page may still need its password. Try again.',
    );
  }
  return { webVisibility: answer.webVisibility, ...(answer.siteUrl ? { siteUrl: answer.siteUrl } : {}) };
}
